/**
 * APEX v7.0 — Coach Messages
 *
 * Logique de sélection du message coach affiché en header du Dashboard.
 *
 * Évalue les conditions dans l'ordre de priorité (1 à 11). Retourne le PREMIER
 * message qui matche. Si aucun ne matche, retourne le message default.
 *
 * Hiérarchie :
 *   1. Tilt detected (loss streak ≥ 4 dans les 24h)
 *   2. Premier login (0 games)
 *   3. Bloc raté (dernière vérif)
 *   4. Bloc validé (dernière vérif)
 *   5. Bloc en cours (≥ 5 games faites)
 *   6. Win streak ≥ 3 dans les 24h
 *   7. LP gain >= +50 sur 7j
 *   8. LP loss <= -50 sur 7j
 *   9. Champion en difficulté (>= 5 games, < 40% WR)
 *  10. Pattern detected (greed, position...)
 *  11. Default
 */

/**
 * Type CoachMessage:
 * {
 *   id: string,             // unique identifier (pour le snooze)
 *   priority: number,       // 1 à 11
 *   tone: 'critical'|'positive'|'neutral'|'warning',
 *   eyebrow: string,        // "COACH APEX · IL Y A 2H"
 *   title: string,          // "🔥 4 victoires d'affilée."
 *   body: string,           // corps du message
 *   ctaPrimary: { label, action },
 *   ctaSecondary?: { label, action }
 * }
 */

import { DAY_MS, WEEK_MS } from '../constants';

/**
 * Calcule un win/loss streak depuis les games les plus récentes.
 * @returns {Object} { type: 'W' | 'L', count: number }
 */
function computeRecentStreak(games, withinMs = DAY_MS) {
  if (!Array.isArray(games) || games.length === 0) return { type: null, count: 0 };

  // Tri descendant par date
  const sorted = [...games].sort((a, b) =>
    new Date(b.timestamp) - new Date(a.timestamp)
  );

  const cutoff = Date.now() - withinMs;
  const recent = sorted.filter(g => new Date(g.timestamp) > cutoff);

  if (recent.length === 0) return { type: null, count: 0 };

  const firstResult = recent[0].result;
  let count = 0;
  for (const g of recent) {
    if (g.result === firstResult) count++;
    else break;
  }
  return { type: firstResult, count };
}

/**
 * Calcule le LP delta sur une période.
 * V7.2.5 : utilise la formule scoreOf au lieu du champ lp_equivalent (qui peut être absent).
 */
function scoreOfSnapshot(s) {
  const tierBase = {
    IRON: 0, BRONZE: 400, SILVER: 800, GOLD: 1200, PLATINUM: 1600,
    EMERALD: 2000, DIAMOND: 2400, MASTER: 2800, GRANDMASTER: 2800, CHALLENGER: 2800
  };
  const rankBase = { IV: 0, III: 100, II: 200, I: 300 };
  const base = tierBase[s.tier] || 0;
  const rankAdd = ['MASTER', 'GRANDMASTER', 'CHALLENGER'].includes(s.tier) ? 0 : (rankBase[s.rank] || 0);
  return base + rankAdd + (s.lp || 0);
}

function computeLpDelta(rankSnapshots, withinMs = WEEK_MS) {
  if (!Array.isArray(rankSnapshots) || rankSnapshots.length === 0) return 0;
  const cutoff = Date.now() - withinMs;
  const inRange = rankSnapshots
    .filter(s => new Date(s.captured_at) > cutoff)
    .sort((a, b) => new Date(a.captured_at) - new Date(b.captured_at));

  if (inRange.length < 2) return 0;
  return scoreOfSnapshot(inRange[inRange.length - 1]) - scoreOfSnapshot(inRange[0]);
}

/**
 * Détecte un champion en difficulté récente.
 */
function findStrugglingChampion(games, minGames = 5, maxWrPct = 40) {
  if (!Array.isArray(games) || games.length === 0) return null;
  const cutoff = Date.now() - WEEK_MS;
  const recent = games.filter(g => new Date(g.timestamp) > cutoff);

  const byChamp = {};
  recent.forEach(g => {
    if (!g.champion) return;
    if (!byChamp[g.champion]) byChamp[g.champion] = { wins: 0, total: 0 };
    byChamp[g.champion].total++;
    if (g.result === 'W') byChamp[g.champion].wins++;
  });

  let worst = null;
  Object.entries(byChamp).forEach(([name, stats]) => {
    if (stats.total < minGames) return;
    const wr = (stats.wins / stats.total) * 100;
    if (wr >= maxWrPct) return;
    if (!worst || wr < worst.wr) {
      worst = { name, wr: Math.round(wr), total: stats.total };
    }
  });
  return worst;
}

/**
 * Trouve le bloc actif et son état (in_progress, completed, failed).
 */
function getActiveBlockState(miniBlocks, games) {
  if (!Array.isArray(miniBlocks) || miniBlocks.length === 0) return null;

  const active = miniBlocks
    .filter(b => b.status === 'active' || b.status === 'in_progress')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  if (!active) {
    // Chercher un bloc récemment résolu (last 24h)
    const recentlyResolved = miniBlocks
      .filter(b => {
        if (!b.resolved_at) return false;
        return Date.now() - new Date(b.resolved_at) < DAY_MS;
      })
      .sort((a, b) => new Date(b.resolved_at) - new Date(a.resolved_at))[0];

    if (recentlyResolved) {
      return {
        block: recentlyResolved,
        status: recentlyResolved.status, // 'completed' | 'failed' | 'abandoned'
        progressGames: recentlyResolved.total_games || 0,
        targetGames: recentlyResolved.target_games || 10,
        isRecentlyResolved: true
      };
    }
    return null;
  }

  // Compter les games depuis création
  const created = new Date(active.created_at);
  const safeGames = Array.isArray(games) ? games : [];
  const gamesAfter = safeGames.filter(g =>
    new Date(g.timestamp) >= created
  ).length;

  return {
    block: active,
    status: 'in_progress',
    progressGames: gamesAfter,
    targetGames: active.target_games || 10,
    isRecentlyResolved: false
  };
}

/**
 * Formate un horodatage relatif court
 */
function formatRelativeTime(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const days = Math.floor(diff / DAY_MS);

  if (hours < 1) return "à l'instant";
  if (hours < 24) return `il y a ${hours}h`;
  if (days === 1) return "hier";
  if (days < 7) return `il y a ${days}j`;
  return new Date(timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

// ============================================================
// LES DETECTORS — Un par condition de la hiérarchie
// ============================================================

function detectTiltStreak(state) {
  const { type, count } = computeRecentStreak(state.games, DAY_MS);
  if (type === 'L' && count >= 4) {
    return {
      id: `tilt-streak-${count}`,
      priority: 1,
      tone: 'critical',
      eyebrow: `COACH APEX · ${count} LOSSES D'AFFILÉE`,
      title: `😬 ${count} losses de suite, ça pique.`,
      body: "On va être honnête : t'es probablement en tilt sans le sentir. Fais une pause de 30 min — t'auras le temps de revenir, et tu reviendras meilleur.",
      ctaPrimary: { label: "OK je coupe", action: 'snooze' },
      ctaSecondary: { label: "Voir l'analyse", action: 'view_analysis' }
    };
  }
  return null;
}

function detectFirstLogin(state) {
  const safeGames = Array.isArray(state?.games) ? state.games : [];
  if (safeGames.length === 0) {
    return {
      id: 'first-login',
      priority: 2,
      tone: 'positive',
      eyebrow: 'COACH APEX · BIENVENUE',
      title: '👋 Salut, content de te voir.',
      body: "APEX est ton coach perso pour grimper. Pour qu'on commence à bosser ensemble, importe ta première game — ça prend 30 secondes et après on attaque.",
      ctaPrimary: { label: "On y va", action: 'open_import' }
    };
  }
  return null;
}

function detectBlockResolved(state) {
  const blockState = getActiveBlockState(state.miniBlocks, state.games);
  if (!blockState?.isRecentlyResolved) return null;

  if (blockState.status === 'completed') {
    return {
      id: `block-completed-${blockState.block.id}`,
      priority: 4,
      tone: 'positive',
      eyebrow: `COACH APEX · ${formatRelativeTime(blockState.block.resolved_at).toUpperCase()}`,
      title: `🔥 Bloc validé. Tu l'as fait.`,
      body: `Objectif : ${blockState.block.target_description || 'ton bloc'}. C'est ce genre de discipline qui sépare ceux qui plafonnent de ceux qui climb. Continue.`,
      ctaPrimary: { label: "Lancer un nouveau bloc", action: 'create_block' },
      ctaSecondary: { label: "Récap détaillé", action: 'view_block' }
    };
  }

  if (blockState.status === 'failed') {
    return {
      id: `block-failed-${blockState.block.id}`,
      priority: 3,
      tone: 'warning',
      eyebrow: `COACH APEX · ${formatRelativeTime(blockState.block.resolved_at).toUpperCase()}`,
      title: `😕 Bloc raté — on ajuste et on reprend.`,
      body: "Pas de mensonge : t'as pas tenu cette fois. Mais ça reste de la data précieuse — t'as appris quelque chose. Essaye un palier intermédiaire avant de revenir sur celui-là. La discipline se construit par paliers.",
      ctaPrimary: { label: "Réajuster l'objectif", action: 'edit_block' },
      ctaSecondary: { label: "Réessayer le même", action: 'retry_block' }
    };
  }

  return null;
}

function detectBlockInProgress(state) {
  const blockState = getActiveBlockState(state.miniBlocks, state.games);
  if (!blockState || blockState.isRecentlyResolved) return null;
  if (blockState.status !== 'in_progress') return null;
  if (blockState.progressGames < 5) return null;

  const remaining = blockState.targetGames - blockState.progressGames;
  if (remaining <= 0) return null;

  return {
    id: `block-progress-${blockState.block.id}-${blockState.progressGames}`,
    priority: 5,
    tone: 'neutral',
    eyebrow: `COACH APEX · BLOC EN COURS`,
    title: `🎯 Plus que ${remaining} games pour valider ton bloc.`,
    body: `Objectif : ${blockState.block.target_description || 'ton bloc'}. T'es à ${blockState.progressGames}/${blockState.targetGames}. Garde le cap.`,
    ctaPrimary: { label: "Voir le bloc", action: 'view_block' }
  };
}

function detectWinStreak(state) {
  const { type, count } = computeRecentStreak(state.games, DAY_MS);
  if (type === 'W' && count >= 3) {
    return {
      id: `win-streak-${count}`,
      priority: 6,
      tone: 'positive',
      eyebrow: `COACH APEX · ${count} WINS D'AFFILÉE`,
      title: `🔥 ${count} victoires d'affilée. T'es en forme.`,
      body: "C'est le moment de capitaliser tant que t'es chaud, mais reste lucide — ne joue pas une de trop par excès de confiance.",
      ctaPrimary: { label: "Importer la suivante", action: 'open_import' },
      ctaSecondary: { label: "Voir l'analyse", action: 'view_analysis' }
    };
  }
  return null;
}

function detectLpGain(state) {
  const delta = computeLpDelta(state.rankSnapshots, WEEK_MS);
  if (delta >= 50) {
    return {
      id: `lp-gain-week-${Math.floor(delta / 50)}`,
      priority: 7,
      tone: 'positive',
      eyebrow: 'COACH APEX · CETTE SEMAINE',
      title: `📈 +${delta} LP cette semaine.`,
      body: "Belle progression. Ce qui compte maintenant : ne pas tout reperdre en lançant une de trop. Reste concentré.",
      ctaPrimary: { label: "Voir la courbe", action: 'view_lp_curve' }
    };
  }
  return null;
}

function detectLpLoss(state) {
  const delta = computeLpDelta(state.rankSnapshots, WEEK_MS);
  if (delta <= -50) {
    return {
      id: `lp-loss-week-${Math.floor(Math.abs(delta) / 50)}`,
      priority: 8,
      tone: 'warning',
      eyebrow: 'COACH APEX · CETTE SEMAINE',
      title: `📉 ${delta} LP cette semaine.`,
      body: "Semaine compliquée. Pas grave, ce qui compte c'est la tendance long terme. On regarde ensemble ce qui a coincé ?",
      ctaPrimary: { label: "Analyser la semaine", action: 'view_analysis' }
    };
  }
  return null;
}

function detectStrugglingChampion(state) {
  const champ = findStrugglingChampion(state.games);
  if (champ) {
    return {
      id: `struggling-champ-${champ.name}-${champ.total}`,
      priority: 9,
      tone: 'warning',
      eyebrow: 'COACH APEX · CHAMPION EN DIFFICULTÉ',
      title: `📉 ${champ.name} en difficulté.`,
      body: `${champ.wr}% de winrate sur tes ${champ.total} dernières games avec ${champ.name}. Soit on bosse dessus avec un bloc focus, soit on le met de côté pour respirer.`,
      ctaPrimary: { label: "Le mettre en sidelined", action: 'sideline_champion', payload: { champion: champ.name } },
      ctaSecondary: { label: "Créer un bloc focus", action: 'create_block', payload: { champion: champ.name } }
    };
  }
  return null;
}

function detectImportTriggerPattern(state) {
  // Pour l'instant, on reste simple. Plus tard, brancher sur detectImportTriggers existant.
  const triggers = state.importTriggers || [];
  if (triggers.length === 0) return null;
  const t = triggers[0];

  return {
    id: `trigger-${t.type}-${t.id || Date.now()}`,
    priority: 10,
    tone: 'warning',
    eyebrow: 'COACH APEX · PATTERN DÉTECTÉ',
    title: t.title || '⚠️ Pattern détecté',
    body: t.body || "On a remarqué quelque chose dans tes dernières games. Jette un œil.",
    ctaPrimary: { label: "Voir le détail", action: 'view_trigger', payload: { trigger: t } }
  };
}

function getDefaultMessage(state) {
  const safeGames = Array.isArray(state?.games) ? state.games : [];
  const total = safeGames.length;
  const lastGameDate = total > 0
    ? new Date(safeGames[0].timestamp)
    : null;

  return {
    id: 'default',
    priority: 11,
    tone: 'neutral',
    eyebrow: lastGameDate
      ? `COACH APEX · DERNIÈRE GAME ${formatRelativeTime(lastGameDate).toUpperCase()}`
      : 'COACH APEX · PRÊT',
    title: total > 0
      ? '🎯 Rien de critique à signaler.'
      : '👋 Salut, prêt à analyser ?',
    body: total > 0
      ? "Tu peux importer tes dernières games ou explorer tes stats. Si t'as joué récemment, c'est le moment de faire le point."
      : "Importe tes games et on commence à regarder ensemble.",
    ctaPrimary: { label: "Importer game", action: 'open_import' },
    ctaSecondary: { label: "Voir le détail", action: 'view_analysis' }
  };
}

// ============================================================
// ORCHESTRATION
// ============================================================

/**
 * Liste ordonnée des détecteurs (par priorité).
 */
const DETECTORS = [
  detectTiltStreak,         // 1
  detectFirstLogin,         // 2
  detectBlockResolved,      // 3 et 4 (failed > completed via priority interne)
  detectBlockInProgress,    // 5
  detectWinStreak,          // 6
  detectLpGain,             // 7
  detectLpLoss,             // 8
  detectStrugglingChampion, // 9
  detectImportTriggerPattern // 10
];

/**
 * Calcule le message coach actuel à partir de l'état de l'app.
 *
 * @param {Object} state - { games, miniBlocks, rankSnapshots, importTriggers, userSettings }
 * @returns {CoachMessage} le message à afficher (jamais null, default en dernier recours)
 */
export function getCoachMessage(state) {
  for (const detector of DETECTORS) {
    const msg = detector(state);
    if (msg) return msg;
  }
  return getDefaultMessage(state);
}

/**
 * Helpers pour gestion du snooze côté UI (à utiliser dans CoachHeader).
 */
const SESSION_KEY = 'apex_coach_snoozed';

export function isSnoozed(messageId) {
  try {
    const snoozed = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]');
    return snoozed.includes(messageId);
  } catch {
    return false;
  }
}

export function snoozeMessage(messageId) {
  try {
    const snoozed = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]');
    if (!snoozed.includes(messageId)) {
      snoozed.push(messageId);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(snoozed));
    }
  } catch (e) {
    console.warn('Failed to snooze coach message', e);
  }
}

export function clearSnoozes() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

export default {
  getCoachMessage,
  isSnoozed,
  snoozeMessage,
  clearSnoozes
};
